import { Card, CardContent } from '@ovComponents/2-component/card/card';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { NumberField } from '@ovComponents/3-pattern/numberField/numberField';
import { OrganizationUserSelect } from '@ovComponents/3-pattern/organizationUserSelect/organizationUserSelect';
import { SwitchField } from '@ovComponents/3-pattern/switchField/switchField';
import { isValidEmail } from '@utils/common';
import { Box, Grid, Tooltip, Typography, useTheme } from 'hephaestus/web';
import { Lock } from 'hephaestus/webIcons';
import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { getDocumentNamesLabel } from './buildSignerRows';
import type { FormSignerMapping } from './formSignerMapping';

const parseSigningOrder = (value: string) => {
  const order = Number.parseInt(value, 10);
  return Number.isNaN(order) ? undefined : order;
};

export type SignerRowProp = {
  signer: FormSignerMapping;
  editSigningOrder?: boolean;
  linkedOrder?: number;
  restrictedRoleNames: string[];
  rolesLoaded: boolean;
  onChange: (changes: Partial<FormSignerMapping>) => void;
};

type SignerRowHeaderActionProp = Pick<
  SignerRowProp,
  'signer' | 'restrictedRoleNames' | 'rolesLoaded' | 'onChange'
>;

const SignerRowHeaderAction = ({
  signer,
  restrictedRoleNames,
  rolesLoaded,
  onChange,
}: SignerRowHeaderActionProp) => {
  const { t } = useTranslation(['workflowCompletions']);
  const theme = useTheme();

  if (!signer.roleProfiles?.length) {
    return (
      <SwitchField
        checked={signer.useOrgUser}
        label={t('manageSigners.useOrgUser')}
        onChange={(checked: boolean) => onChange({ useOrgUser: checked })}
      />
    );
  }

  return (
    <Tooltip
      title={
        rolesLoaded
          ? `${t('manageSigners.restrictedToRoles')}: ${restrictedRoleNames.join(', ')}`
          : undefined
      }
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          color: theme.color.onSurfaceVariant,
          cursor: 'help',
        }}
      >
        <Lock fontSize='small' />
        <Typography
          variant='bodyLarge'
          sx={{
            color: theme.color.onSurfaceVariant,
            textDecoration: 'underline dotted',
            textUnderlineOffset: '3px',
          }}
        >
          {t('manageSigners.restrictedByRole')}
        </Typography>
      </Box>
    </Tooltip>
  );
};

type SignerFieldsProp = Pick<
  SignerRowProp,
  'signer' | 'editSigningOrder' | 'onChange'
>;

const OrgUserFields = ({
  signer,
  editSigningOrder,
  onChange,
}: SignerFieldsProp) => {
  const { t } = useTranslation(['workflowCompletions']);

  return (
    <Grid container spacing={2} sx={{ mt: 1 }}>
      <Grid size={{ xs: 12, sm: editSigningOrder ? 9 : 12 }}>
        <OrganizationUserSelect
          label={t('manageSigners.user')}
          value={signer.organizationUserId ?? ''}
          filter={{
            roleProfileIds: signer.roleProfiles?.length
              ? signer.roleProfiles.map((r) => r.id)
              : undefined,
          }}
          // biome-ignore lint/suspicious/noExplicitAny: the select has no typed option
          onChange={(value: any) =>
            onChange({
              organizationUserId: value?.id,
              firstName: value?.firstName,
              lastName: value?.lastName,
              email: value?.email,
            })
          }
        />
      </Grid>
      {editSigningOrder && (
        <Grid size={{ xs: 12, sm: 3 }}>
          <NumberField
            sx={{ width: '100px' }}
            label={t('manageSigners.signingOrder')}
            number={signer.signingOrder?.toString() ?? ''}
            setNumber={(value: string) =>
              onChange({ signingOrder: parseSigningOrder(value) })
            }
          />
        </Grid>
      )}
    </Grid>
  );
};

const ManualFields = ({
  signer,
  editSigningOrder,
  onChange,
}: SignerFieldsProp) => {
  const { t } = useTranslation(['workflowCompletions']);
  const emailIsInvalid =
    !!signer.email?.trim() && !isValidEmail(signer.email.trim());

  return (
    <Grid container spacing={2} sx={{ mt: 1 }}>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          label={t('manageSigners.firstName')}
          value={signer.firstName}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onChange({ firstName: e.target.value })
          }
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          label={t('manageSigners.lastName')}
          value={signer.lastName}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onChange({ lastName: e.target.value })
          }
        />
      </Grid>
      <Grid size={{ xs: 12, sm: editSigningOrder ? 9 : 12 }}>
        <TextField
          fullWidth
          label={t('manageSigners.email')}
          value={signer.email}
          error={emailIsInvalid}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onChange({ email: e.target.value })
          }
        />
      </Grid>
      {editSigningOrder && (
        <Grid size={{ xs: 12, sm: 3 }}>
          <NumberField
            label={t('manageSigners.signingOrder')}
            number={signer.signingOrder?.toString() ?? ''}
            setNumber={(value: string) =>
              onChange({ signingOrder: parseSigningOrder(value) })
            }
          />
        </Grid>
      )}
    </Grid>
  );
};

export const SignerRow = ({
  signer,
  editSigningOrder,
  linkedOrder,
  restrictedRoleNames,
  rolesLoaded,
  onChange,
}: SignerRowProp) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['workflowCompletions']);
  const isUserBound = !!signer.signerUserId;
  const showOrgUserFields = signer.useOrgUser && !isUserBound;
  const Fields = showOrgUserFields ? OrgUserFields : ManualFields;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: '100%',
      }}
    >
      <Card variant='outlined' sx={{ width: '100%' }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              justifyContent: 'space-between',
            }}
          >
            <Typography variant='bodyLarge'>
              {signer.signerLabel ?? translateBackendString(signer.signerName)}
            </Typography>
            {!isUserBound && (
              <SignerRowHeaderAction
                signer={signer}
                restrictedRoleNames={restrictedRoleNames}
                rolesLoaded={rolesLoaded}
                onChange={onChange}
              />
            )}
          </Box>
          {!!signer.documentNames?.length && (
            <Typography variant='bodySmall' color='textSecondary'>
              {t('manageSigners.signingDocuments')}{' '}
              {getDocumentNamesLabel(
                signer.documentNames,
                translateBackendString,
              )}
            </Typography>
          )}
          {linkedOrder !== undefined && (
            <Typography variant='bodySmall' color='textSecondary'>
              {t('manageSigners.linkedSigner', { order: linkedOrder })}
            </Typography>
          )}
          <Fields
            signer={signer}
            editSigningOrder={editSigningOrder}
            onChange={onChange}
          />
        </CardContent>
      </Card>
    </Box>
  );
};
